/**
 * The French dictionary — the language every one of our sentences is authored in, and the fallback for all
 * the others (feature 0050).
 *
 * ── House rules ────────────────────────────────────────────────────────────────────────────────────────
 *
 * **The key's prefix is the category the seller sees** in the text editor. `cart.` groups under Panier,
 * `checkout.` under Commande, `a11y.` under Accessibilité. So a prefix is a product decision, not a folder.
 *
 * **A sentence lives here once.** If two components show "Ajouter au panier", they share the key. The rule
 * the spec calls "un seul domicile" — and the reason it has to be enforced rather than assumed is that the
 * codebase already broke it: the COD form's labels exist twice, once in ui-react and once again in the
 * dashboard's Angular preview of the same form.
 *
 * **Write the sentence, not the fragment.** No `'restant' + (n > 1 ? 's' : '')`: that is a French suffix
 * glued onto a word, and no other language can be built from it. Counted sentences get plural forms, and
 * Arabic will need six of them.
 *
 * **Punctuation belongs to the sentence.** The ellipsis in "Ajout en cours…", the middle dot in
 * "Paiement sécurisé · 249 MAD" — a translator has to be able to move or drop them.
 */
export const fr = {
    // ── Accessibilité — lus par les lecteurs d'écran, jamais affichés ────────────────────────────────────
    'a11y.previous_image': 'Image précédente',
    'a11y.next_image': 'Image suivante',
    'a11y.previous_slide': 'Diapositive précédente',
    'a11y.next_slide': 'Diapositive suivante',
    'a11y.close': 'Fermer',
    'a11y.flip_card': 'Plus de détails',
    'a11y.open_menu': 'Ouvrir le menu',
    'a11y.close_menu': 'Fermer le menu',
    // Verbatim what the four steppers render today. They deserve a fuller label ("Diminuer la quantité"),
    // but this lot's rule is a rendering identical to the commit before it — improving copy is a change to
    // list and make deliberately, not one to smuggle in under a refactor.
    'a11y.decrease_quantity': 'Diminuer',
    'a11y.increase_quantity': 'Augmenter',
    'a11y.play_video': 'Lire la vidéo',
    'a11y.pause_video': 'Mettre la vidéo en pause',
    'a11y.mute_video': 'Couper le son',
    'a11y.unmute_video': 'Activer le son',
    'a11y.fullscreen': 'Plein écran',
    'a11y.special_offer': 'Offre spéciale',
    'a11y.variants': 'Variantes',
    'a11y.payment_method': 'Mode de paiement',
    'a11y.home': 'Accueil',
    'a11y.account': 'Compte',
    'a11y.cart': 'Panier',
    'a11y.menu': 'Menu',
    'a11y.mobile_menu': 'Menu mobile',
    // « Primary » est le nom de la navigation principale pour un lecteur d'écran. Il était en anglais dans
    // le port Shrine ; c'est une phrase entendue par un acheteur, donc elle suit la langue de la boutique.
    'a11y.primary_navigation': 'Navigation principale',
    'a11y.previous_thumbnails': 'Miniatures précédentes',
    'a11y.next_thumbnails': 'Miniatures suivantes',
    'a11y.zoom_image': "Agrandir l'image",
    'a11y.image_gallery': "Galerie d'images",
    'a11y.go_to_image': "Aller à l'image {index}",
    // ── Panier ──────────────────────────────────────────────────────────────────────────────────────────
    'cart.title': 'Panier',
    'cart.view': 'Voir le panier',
    'cart.added': 'Ajouté ✓',
    'cart.quantity': 'Quantité',
    'cart.remove': 'Retirer',
    'cart.subtotal': 'Sous-total',
    'cart.subtotal_with_units': 'Sous-total ({units})',
    'cart.shipping': 'Livraison',
    'cart.shipping_free': 'Gratuite',
    'cart.you_save': 'Vous économisez',
    'cart.upsell_badge': 'Offre',
    'cart.total': 'Total',
    'cart.view_arrow': 'Voir le panier →',
    'cart.remove_named': 'Retirer {name}',
    'cart.promo_code_label': 'Code {code}',
    'cart.remove_promo': 'Retirer le code promo',
    'cart.empty': 'Votre panier est vide.',
    // Lignes que la boutique ne peut plus vendre (22/09/2026) — le devis du panier les NOMME, la
    // page les barre, les sort du total et propose de les retirer. Sans ces phrases, l'acheteuse
    // lisait « un article n'est plus disponible » sans jamais savoir lequel, ni comment s'en sortir.
    'cart.line_unavailable': 'Indisponible',
    'checkout.items_unavailable': {
        one: '{count} article de votre panier n’est plus disponible.',
        other: '{count} articles de votre panier ne sont plus disponibles.',
    },
    'checkout.remove_unavailable': 'Retirer les articles indisponibles',
    'checkout.remove_unavailable_first': 'Retirez les articles indisponibles pour continuer.',
    'cart.continue_shopping': 'Continuer mes achats',
    'cart.back_to_cart': 'Retour au panier',
    'cart.back_to_cart_arrow': '← Retour au panier',
    'cart.item_count': {
        zero: 'Aucun article',
        one: '{count} article',
        other: '{count} articles',
    },
    // ── Pied de panier ──────────────────────────────────────────────────────────────────────────────────
    //
    // Ces phrases sont arrivées EN ANGLAIS avec le port Shrine — la consigne était « CSS verbatim », et la
    // copie a suivi. Une boutique française affichait « Checkout » sur son bouton de commande. Ce sont les
    // seules valeurs de ce lot qui changent ce qui s'affiche, et c'est le but.
    'cart.checkout': 'Commander',
    'cart.product_discounts': 'Remises produits −{amount}',
    'cart.discount_code_placeholder': 'Saisir un code de réduction',
    'cart.discount_add': 'AJOUTER',
    'cart.order_note': 'Note de commande',
    'cart.order_note_placeholder': 'Ajouter une note à votre commande…',
    // `[amount]` et `[next_goal]` sont des JETONS que le vendeur voit et déplace dans l'inspecteur : le code
    // fait un `replace` dessus. Ils ne se traduisent pas — la phrase autour, si.
    'cart.free_shipping_reached': 'Bravo ! La livraison vous est offerte.',
    'cart.free_shipping_progress': 'Plus que [amount] pour la livraison offerte !',
    'cart.checkpoints_all_reached': 'Bravo ! Vous avez débloqué toutes les récompenses.',
    'cart.checkpoints_progress': 'Plus que [amount] pour [next_goal] !',
    'cart.goal_free_shipping': 'Livraison offerte',
    'cart.goal_discount': 'une remise de 20 %',
    'cart.goal_free_gift': 'un CADEAU OFFERT',
    // Les trois refus du champ « code de réduction ». Le vendeur peut les réécrire ; ce sont les valeurs de
    // départ, et elles étaient françaises quelle que soit la langue de la boutique.
    'cart.discount_error_empty': 'Veuillez saisir un code promo !',
    'cart.discount_error_not_applicable': 'Ce code n’est pas valide ou ne s’applique pas à votre panier.',
    'cart.discount_error_already_applied': 'Ce code est déjà appliqué à votre commande.',
    'cart.terms_accept': 'J’accepte les conditions générales.',
    'cart.terms_required': 'Vous devez accepter les conditions générales.',
    // ── Page collection : filtres et tri ────────────────────────────────────────────────────────────────
    // Anglaises elles aussi, pour la même raison.
    'collection.go_to_product': 'Voir le produit',
    'collection.filter_heading': 'Filtrer :',
    'collection.availability': 'Disponibilité',
    'collection.price': 'Prix',
    'collection.in_stock': 'En stock',
    'collection.out_of_stock': 'Rupture de stock',
    'collection.sort_featured': 'En vedette',
    'collection.sort_best_selling': 'Meilleures ventes',
    'collection.sort_title_asc': 'Alphabétique, A-Z',
    'collection.sort_title_desc': 'Alphabétique, Z-A',
    'collection.sort_price_asc': 'Prix, du plus bas au plus haut',
    'collection.sort_price_desc': 'Prix, du plus haut au plus bas',
    'collection.sort_created_desc': 'Date, du plus récent au plus ancien',
    'collection.price_range_slider': 'Curseur de fourchette de prix',
    'collection.maximum_price': 'Prix maximum',
    'collection.reset': 'Réinitialiser',
    'collection.sort_by_label': 'Trier par :',
    'collection.sort_by': 'Trier par',
    'collection.remove_all': 'Tout retirer',
    'collection.no_products': 'Aucun produit trouvé',
    'collection.try_removing_filter': 'Retirez un filtre pour voir plus de {name}.',
    'collection.clear_all_filters': 'Effacer tous les filtres',
    'collection.load_more': 'Voir plus',
    'collection.filter_and_sort': 'Filtrer et trier',
    'collection.clear': 'Effacer',
    'collection.clear_all': 'Tout effacer',
    'collection.apply': 'Appliquer',
    'collection.product_count': {
        zero: 'Aucun produit',
        one: '{count} produit',
        other: '{count} produits',
    },
    'collection.selected_count': {
        one: '{count} sélectionné',
        other: '{count} sélectionnés',
    },
    // ── En-tête de la boutique ──────────────────────────────────────────────────────────────────────────
    'header.search': 'Rechercher',
    'header.search_placeholder': 'Rechercher un produit…',
    'header.menu': 'Menu',
    'header.account_login': 'Compte / Connexion',
    // ── Fiche produit ───────────────────────────────────────────────────────────────────────────────────
    'product.add_to_cart': 'Ajouter au panier',
    'product.buy_now': 'Acheter maintenant',
    'product.sold_out': 'Rupture de stock',
    // Le suffixe collé au libellé d'une variante indisponible dans le menu déroulant — « Bleu — épuisé ».
    // Une clé à part de `product.sold_out` : ici c'est un fragment accolé, pas une phrase.
    'product.sold_out_suffix': ' — épuisé',
    'product.savings_percent': 'Économisez {percent}%',
    'product.savings_amount': 'Économisez {amount}',
    'product.size_guide': 'Guide des tailles',
    // ── Commande & paiement ─────────────────────────────────────────────────────────────────────────────
    'checkout.pay_secure': 'Paiement sécurisé',
    'checkout.pay_on_delivery': 'Paiement à la livraison',
    'checkout.pay_secure_with_total': 'Paiement sécurisé · {total}',
    'checkout.pay_on_delivery_with_total': 'Paiement à la livraison · {total}',
    'checkout.redirecting': 'Redirection vers le paiement sécurisé…',
    'checkout.confirming': 'Confirmation…',
    'checkout.pay_by_card': 'Payer par carte',
    'checkout.sending': 'Envoi…',
    'checkout.order_cod_cta': 'Commander — paiement à la livraison',
    'checkout.card_label': 'Carte bancaire',
    'checkout.order_received': 'Commande reçue',
    // Split around the phone number, which is a `<span>` in the middle of the sentence. Two keys rather than
    // one with a `{phone}` placeholder, because the placeholder would have to carry markup — and a language
    // that puts the number first still gets both halves to move.
    'checkout.we_will_call_before': 'Nous vous appellerons au ',
    'checkout.we_will_call_after': ' pour confirmer la livraison.',
    'checkout.edit_order': 'Modifier ma commande',
    'checkout.contact': 'Coordonnées',
    'checkout.shipping_method': 'Mode de livraison',
    'checkout.standard_shipping': 'Livraison standard',
    'checkout.payment': 'Paiement',
    'checkout.all_transactions_secure': 'Toutes les transactions sont sécurisées et chiffrées.',
    'checkout.secure_encrypted': 'Paiement sécurisé et chiffré',
    'checkout.loading_secure_payment': 'Chargement du paiement sécurisé…',
    'checkout.pay_now': 'Payer maintenant',
    'checkout.processing': 'Traitement…',
    'checkout.loading': 'Chargement…',
    'checkout.no_payment_method': 'Cette boutique n’accepte aucun moyen de paiement pour le moment.',
    'checkout.summary_heading': 'Récapitulatif de la commande',
    'checkout.show_summary': 'Afficher le récapitulatif',
    'checkout.hide_summary': 'Masquer le récapitulatif',
    'checkout.country_region': 'Pays / région',
    // Les huit libellés du formulaire de livraison. Ils étaient classés « chrome du builder » par le
    // recensement — `label` est un nom de l'inspecteur dans un schéma — donc invisibles dans la liste de
    // travail, alors qu'ils sont sur la page où l'acheteur saisit son adresse pour payer.
    'checkout.email_optional': 'E-mail (facultatif)',
    'checkout.first_name_optional': 'Prénom (facultatif)',
    'checkout.last_name': 'Nom',
    'checkout.address': 'Adresse',
    'checkout.address_line2_optional': 'Appartement, étage, etc. (facultatif)',
    'checkout.zip_optional': 'Code postal (facultatif)',
    'checkout.city': 'Ville',
    'checkout.phone': 'Téléphone',
    // L'aide sous le téléphone. Elle était une phrase française écrite en dur dans l'îlot, donc
    // française sous un formulaire anglais (05/10/2026).
    'checkout.phone_hint': 'Pour être joint au sujet de la livraison.',
    'checkout.total_savings': 'ÉCONOMIES TOTALES',
    'checkout.discount_code': 'Code de réduction',
    // Split around the code, which is a styled `<span>` inside the sentence.
    'checkout.discount_prefix': 'Réduction (',
    'checkout.discount_suffix': ')',
    'checkout.thanks_title': 'Merci pour votre commande !',
    'checkout.thanks_before': 'Commande ',
    'checkout.thanks_after': ' enregistrée. Vous payez à la livraison.',
    // Card errors. `checkout.card_unavailable_choose_cod` differs from `error.card_unavailable` on purpose:
    // at the checkout the buyer HAS a second method to fall back to, so the sentence names it. In the buy-box
    // they may not, so it only says to retry.
    'checkout.card_unavailable_choose_cod': 'Le paiement par carte est momentanément indisponible. Réessayez, ou choisissez le paiement à la livraison.',
    'checkout.card_amount_too_small': 'Le montant est trop faible pour un paiement par carte. Ajoutez un article, ou choisissez le paiement à la livraison.',
    'checkout.check_card_details': 'Vérifiez vos informations de carte et réessayez.',
    'checkout.payment_not_confirmed': 'Le paiement n’a pas pu être confirmé. Vérifiez vos informations.',
    // Deux voisines des précédentes, à un mot près. Gardées à part parce que ce lot rend à l'identique : les
    // réunir changerait silencieusement l'une des deux formulations, ce qui n'est pas une migration.
    'checkout.card_unavailable_choose_cod_alt': 'Le paiement par carte est momentanément indisponible. Vous pouvez commander en paiement à la livraison.',
    'checkout.payment_not_confirmed_retry': 'Le paiement n’a pas pu être confirmé. Vérifiez vos informations et réessayez.',
    'checkout.keep_card_note': 'Votre carte est conservée par la boutique pour compléter cette commande en un clic. Aucun autre usage.',
    'checkout.cod_note_delivery': 'Vous réglez en espèces au livreur, à la réception de votre commande.',
    // Order refusals. The 422 is uniform by design — the server never says which check failed — so the two
    // wordings differ only by whether a discount code was applied, which is the one thing we do know.
    'checkout.check_highlighted_fields': 'Veuillez vérifier les champs en surbrillance.',
    'checkout.order_failed_with_promo': 'La commande n’a pas pu être passée. Vérifiez votre panier et votre code de réduction puis réessayez.',
    'checkout.order_failed_item_gone': 'Un article de votre panier n’est plus disponible. Vérifiez votre panier puis réessayez.',
    'checkout.order_failed_retry': 'La commande n’a pas pu être passée. Réessayez dans un instant.',
    'checkout.promo_check_failed': 'Impossible de vérifier le code. Réessayez dans un instant.',
    'checkout.promo_not_combinable': 'Ce code n’est pas cumulable avec l’offre déjà appliquée à votre panier.',
    'checkout.promo_invalid': 'Ce code n’est pas valide ou n’est plus disponible.',
    'checkout.card_note': 'Paiement sécurisé par carte bancaire.',
    'checkout.cod_note': 'Aucun paiement en ligne — payez en espèces à la réception.',
    'checkout.order_confirmed': 'Commande confirmée. Vous serez contacté pour la livraison.',
    'checkout.order_confirmed_with_number': 'Commande confirmée — n° {number}{total}. Vous serez contacté pour la livraison.',
    'checkout.order_recorded_demo': 'Commande enregistrée (démo). Le paiement à la livraison sera confirmé par téléphone.',
    // ── Erreurs de commande — ce que l'acheteur lit quand ça casse ───────────────────────────────────────
    //
    // Elles comptent plus que le reste : un acheteur qui ne comprend pas l'erreur abandonne. Le français y
    // était déjà en dur, dans un `switch` de codes serveur — donc invisible pour un vendeur arabe.
    'error.generic': 'Un problème est survenu. Réessayez dans un instant.',
    'error.already_paid': 'Cette commande a déjà été réglée ou n’est plus payable.',
    // Two shapes of the same idea, kept apart because they are punctuated differently on screen: the promo
    // field shows an em-dash clause inline, the checkout shows two sentences. Merging them would silently
    // change one of the two renderings, which this lot is not allowed to do.
    'error.too_many_attempts': 'Trop de tentatives — patientez un instant puis réessayez.',
    'error.too_many_attempts_dot': 'Trop de tentatives. Patientez un instant puis réessayez.',
    'error.card_unavailable': 'Le paiement par carte est momentanément indisponible. Réessayez dans un instant.',
    'error.check_your_details': 'Veuillez vérifier vos coordonnées.',
    'error.invalid_phone': 'Numéro invalide — 9 chiffres attendus (ex. 6 12 34 56 78)',
    // ── Formulaires ─────────────────────────────────────────────────────────────────────────────────────
    'form.required': 'Champ requis',
    'form.comment': 'Commentaire',
    'form.custom_field': 'Champ personnalisé',
    // ── Builder — vu par le VENDEUR dans l'éditeur, jamais par un acheteur ───────────────────────────────
    //
    // Une exception assumée à « le dashboard reste en français » : ce texte est rendu par le MÊME composant
    // que la vitrine, donc il ne peut pas vivre ailleurs sans dupliquer le composant.
    'builder.cod_app_off': 'App « Cash on Delivery » désactivée — la boîte d’achat affiche le panier. Installe-la dans Apps pour retrouver le formulaire de commande.',
    // Les trois textes de la DÉMO — l'aperçu du builder et le mode hors-ligne, jamais une vraie boutique.
    // Le dernier est en anglais et le reste : il s'adresse au vendeur dans son éditeur, pas à un acheteur.
    'builder.added_to_cart_demo': 'Ajouté au panier (démo).',
    'builder.redirect_payment_demo': 'Redirection vers le paiement (démo).',
    'builder.sample_product_note': 'Produit d’exemple — le vrai produit, son prix et ses variantes apparaissent sur la page en ligne.',
    'builder.sample_gallery_note': 'Produit d’exemple — les vraies images du produit apparaissent sur la page en ligne.',
    'builder.sizing_table_hint': 'Ajoutez des blocs « Ligne du tableau » dans l’arborescence de gauche pour construire le tableau — la première ligne est l’en-tête.',
    'builder.volume_tiers_empty_title': 'Paliers de lot',
    'builder.volume_tiers_empty_body': 'créez une offre de quantité pour ce produit afin d’afficher ici les paliers sélectionnables.',
    'builder.bundle_pick_all': 'Choisissez un produit pour chaque bloc ajouté',
    // ── Formulaire COD & upsells ────────────────────────────────────────────────────────────────────────
    'cod.unit_count': {
        one: '{count} unité',
        other: '{count} unités',
    },
    'cod.promo_placeholder': 'Code promo',
    'cod.promo_apply': 'Appliquer',
    'cod.promo_checking': 'Vérification…',
    'cod.promo_toggle': 'Vous avez un code promo ?',
    'cod.promo_invalid': 'Code invalide ou expiré.',
    'cod.promo_not_combinable': 'Ce code ne peut pas être combiné avec l’offre en cours.',
    'cod.promo_check_failed': 'Vérification impossible. Réessayez dans un instant.',
    'cod.promo_online_only': 'Les codes se vérifient sur la boutique en ligne.',
    'cod.upsell_step': 'Offre {index} sur {total}',
    'cod.upsell_skip': 'Passer',
    'cod.upsell_choice': 'Choix',
    'cod.upsell_accept': 'Oui, ajouter à ma commande',
    'cod.upsell_decline': 'Non merci',
    'cod.upsell_adding': 'Ajout en cours…',
    'cod.upsell_sold_out': 'épuisé',
    'cod.upsell_order_placed': 'Votre commande est enregistrée. Un clic ajoute cette offre, rien à re-remplir.',
    'cod.upsell_added': 'C’est ajouté à votre commande — le nouveau total est celui affiché ci-dessous.',
    'cod.upsell_gone': 'Cette offre n’est plus disponible. Votre commande n’a pas changé.',
    'cod.upsell_failed': 'Nous n’avons pas pu ajouter cette offre. Votre commande n’a pas changé — vous pouvez continuer.',
    'cod.offer_up_to': 'Jusqu’à −{percent} %',
    // Les valeurs de DÉPART du formulaire COD (famille C). Le vendeur peut toutes les réécrire dans
    // l'éditeur ; ce sont celles qu'il trouve en ouvrant l'app pour la première fois, et elles étaient
    // françaises quelle que soit la langue de sa boutique.
    'cod.default_name_placeholder': 'Votre nom',
    'cod.default_city_placeholder': 'Votre ville',
    'cod.default_address_placeholder': 'Rue, quartier, étage, point de repère…',
    'cod.default_note_placeholder': 'Instructions de livraison…',
    'cod.default_offers_title': 'Choisissez votre pack',
    'cod.default_offer_badge': 'Le + populaire',
    'cod.default_bump_title': 'Ajoutez une garantie 2 ans',
    'cod.default_bump_description': 'Protection casse & défauts, sans conditions.',
    'cod.default_button_label': 'Commander — Paiement à la livraison',
    'cod.default_confirmation_message': 'Merci ! Votre commande est confirmée. Nous vous appellerons pour confirmer la livraison.',
    'cod.default_field_name': 'Nom complet',
    'cod.default_field_phone': 'Téléphone',
    'cod.default_field_city': 'Ville',
    'cod.default_field_address': 'Adresse',
    'cod.default_field_note': 'Note (facultatif)',
    'cod.field_type_text': 'Champ texte',
    'cod.field_type_email': 'Email',
    'cod.field_type_number': 'Nombre',
    'cod.field_type_textarea': 'Zone de texte',
    'cod.field_type_select': 'Choix',
    'cod.field_type_new': 'Nouveau champ',
    // Les valeurs de départ de la SECTION boîte d'achat COD (famille B) — le titre du formulaire, et les
    // sept phrases que le preset « COD buy-box » écrit dans les blocs d'un nouvel encart : trois arguments
    // produit et trois puces de réassurance. Le vendeur les réécrit toutes ; il fallait juste qu'elles ne
    // soient pas françaises dans une boutique arabe.
    'cod.form_title': 'Vos coordonnées de livraison',
    'cod.benefit_premium_material': 'Matière premium, coupe ajustée',
    'cod.benefit_fast_shipping': 'Expédié sous 24–48h',
    'cod.benefit_easy_exchange': 'Échange facile sous 14 jours',
    'cod.trust_returns_14_days': 'Retours sous 14 jours',
    'cod.trust_ships_everywhere': 'Livraison partout',
    // ── Page de remerciement (commande COD) ─────────────────────────────────────────────────────────────
    // ` ` = espace insécable : en français le « ! » ne se sépare jamais du mot qui le précède, et le
    // gabarit rendait déjà `&nbsp;!`. L'écrire en échappement plutôt qu'en caractère invisible pour que la
    // prochaine personne qui édite cette ligne voie ce qu'elle déplace.
    'confirmation.page_title': 'Merci pour votre commande',
    'confirmation.thanks': 'Merci !',
    'confirmation.thanks_named': 'Merci, {name} !',
    'confirmation.order_before': 'Votre commande ',
    'confirmation.order_after': ' est bien confirmée.',
    'confirmation.cod_pill': 'Paiement à la livraison — aucun paiement en ligne',
    // Pluriel réel : l'offre refusée est comptée. `zero` n'est jamais atteint (la ligne est masquée à 0),
    // mais le laisser vide ferait rendre la clé si un jour elle l'était.
    'confirmation.dropped_upsells': {
        zero: 'Toutes les offres que vous aviez acceptées ont été ajoutées à votre commande.',
        one: 'Une offre que vous aviez acceptée n’était plus disponible — elle n’a pas été ajoutée à votre commande.',
        other: '{count} offres que vous aviez acceptées n’étaient plus disponibles — elles n’ont pas été ajoutées à votre commande.',
    },
    'confirmation.your_order': 'Votre commande',
    'confirmation.due_on_delivery': 'À payer à la livraison',
    'confirmation.paid': 'Payé',
    'confirmation.cash_on_receipt': 'En espèces, à la réception',
    // « Livraison » l'en-tête de la carte adresse, PAS « Livraison » la ligne de frais de port
    // (`cart.shipping`). Même mot en français, deux phrases différentes : en arabe le titre de section est
    // التوصيل et la ligne de frais رسوم الشحن. Une seule clé pour les deux les figerait ensemble.
    'confirmation.delivery_section': 'Livraison',
    'confirmation.address': 'Adresse',
    'confirmation.we_will_call': 'Nous vous appellerons',
    'confirmation.we_will_call_rest': ' au {phone} pour confirmer votre commande avant l’expédition.',
    'confirmation.trust_delivery': 'Livraison 24–48h',
    'confirmation.trust_pay_on_receipt': 'Payez à la réception',
    'confirmation.trust_call': 'Confirmation par appel',
    'confirmation.trust_returns': 'Retour sous 7 jours',
    'confirmation.not_found': 'Nous n’avons pas retrouvé cette commande.',
    'confirmation.back_to_store': 'Retour à la boutique',
    // ── Retour de paiement par carte (Stripe redirige ici) ──────────────────────────────────────────────
    'return.checking_title': 'Vérification de votre paiement',
    'return.checking_body': 'Merci de patienter un instant.',
    'return.received_title': 'Paiement reçu',
    'return.received_body': 'Nous finalisons votre commande. Une confirmation vous parviendra dans un instant.',
    'return.processing_title': 'Paiement en cours de traitement',
    'return.processing_body': 'Votre paiement est en cours de vérification. Vous recevrez une confirmation sous peu.',
    'return.failed_title': 'Le paiement n’a pas abouti',
    'return.failed_body': 'Aucun montant n’a été confirmé. Vous pouvez réessayer depuis la page du produit.',
    'return.unknown_title': 'Merci',
    'return.unknown_body': 'Nous confirmons votre commande. Vous recevrez un e-mail dès qu’elle est validée.',
    'return.back_to_store': '← Retour à la boutique',
    // ── Panier : tiroir latéral ─────────────────────────────────────────────────────────────────────────
    // « Votre panier est vide » sans point ici, avec un point dans `cart.empty` : deux surfaces, deux
    // ponctuations, et ce lot rend à l'identique. À réunir quand quelqu'un décidera laquelle est la bonne.
    'cart.your_cart': 'Votre panier',
    'cart.empty_title': 'Votre panier est vide',
    'cart.empty_body': 'Vous n’avez encore rien ajouté.',
    'a11y.close_cart': 'Fermer le panier',
    'a11y.variant': 'Variante',
    'a11y.sticky_add_to_cart': 'Bouton d’achat fixe',
    // ── Fiche produit (suite) ───────────────────────────────────────────────────────────────────────────
    'product.variant_sold_out': '{name} (épuisé)',
    'product.on_sale': 'Solde',
    // ── Formulaire de contact ───────────────────────────────────────────────────────────────────────────
    // Anglais lui aussi jusqu'ici : un visiteur français remplissait « Name / Email / Phone » et lisait
    // « This field is required » sous le champ qu'il avait oublié.
    'contact.name': 'Nom',
    'contact.email': 'E-mail',
    'contact.phone': 'Téléphone',
    'contact.custom_field': 'Champ personnalisé',
    'contact.send': 'Envoyer',
    'contact.field_required': 'Ce champ est obligatoire',
    'contact.send_failed': 'Nous n’avons pas pu envoyer votre message. Réessayez.',
    // ── Avis Facebook ───────────────────────────────────────────────────────────────────────────────────
    // La section imite l'interface de Facebook, qui est traduite : en français elle dit « J'aime », pas
    // « Like ». Garder l'anglais ne serait pas de la fidélité à la maquette, ce serait une maquette anglaise.
    'reviews.like': 'J’aime',
    'reviews.comment': 'Commenter',
    'reviews.reply': 'Répondre',
    'reviews.share': 'Partager',
    // ── Blog ────────────────────────────────────────────────────────────────────────────────────────────
    'blog.view_all': 'Voir tout',
    'blog.placeholder_title': 'Article de blog',
    'blog.placeholder_summary': 'Donnez à vos clients un résumé de votre article.',
    // ── Blocs de la fiche produit ───────────────────────────────────────────────────────────────────────
    'product.offers': 'Offres',
    'product.per_unit': 'l’unité',
    'product.offer_unit': 'Unité {n}',
    'product.sold_out_short': 'Épuisé',
    'product.high_demand': 'Forte demande',
    'product.special_offer_banner': 'OFFRE SPÉCIALE',
    'product.urgency_banner': 'PRODUIT POPULAIRE | STOCK LIMITÉ',
    'product.details': 'Détails du produit',
    'product.text_with_icon': 'Texte avec icône',
    'product.secure_payment_100': 'Paiement 100% sécurisé',
    'product.shipping_info': 'Informations de livraison',
    'product.new_arrivals': 'Nouveautés',
    'product.link_copied': 'Lien copié',
    'product.save_percentage': 'ÉCONOMISEZ [percentage]',
    'product.us_column': 'Nous',
    'product.yes': 'Oui',
    // ── Accessibilité (suite) ───────────────────────────────────────────────────────────────────────────
    'a11y.previous': 'Précédent',
    'a11y.next': 'Suivant',
    'a11y.countdown': 'Compte à rebours',
    'a11y.page_navigation': 'Navigation de la page',
    'a11y.faq_categories': 'Catégories de questions',
    'a11y.before_after': 'Comparaison avant / après',
    'a11y.tabs': 'Onglets',
    'a11y.vertical_ticker': 'Bandeau défilant',
    'a11y.previous_slides': 'Diapositives précédentes',
    'a11y.next_slides': 'Diapositives suivantes',
    // ── Pied de page & inscription ──────────────────────────────────────────────────────────────────────
    'footer.email_placeholder': 'Votre e-mail',
    'footer.subscribe': 'S’abonner',
    'footer.subscribed': 'Merci ! Vous êtes inscrit(e).',
    'footer.subscribe_error': 'Impossible pour le moment, réessayez.',
    'footer.email_invalid': 'Entrez une adresse e-mail valide.',
    // ── Fenêtre de bienvenue (0072 lot 6) — le seul point de capture d'adresse avant la caisse ─────────
    'welcome_popup.title': 'Rejoignez notre liste',
    'welcome_popup.subtitle': 'Nos nouveautés et nos offres, directement par e-mail.',
    'welcome_popup.consent': 'En vous inscrivant, vous acceptez de recevoir nos e-mails. Vous pouvez vous désinscrire à tout moment.',
    'welcome_popup.code': 'Votre code : {code}',
    'footer.social': 'Réseaux sociaux',
    'footer.payment_methods': 'Moyens de paiement',
    'newsletter.email': 'E-mail',
    'newsletter.thanks': 'Merci pour votre inscription.',
    // Les valeurs de DÉPART du pied de page et de sa colonne d'inscription (famille B). `{year}` est un
    // paramètre du code, pas un jeton d'inspecteur : il ne se traduit pas, la phrase autour, si.
    'footer.tagline': 'Des produits que vous allez adorer, livrés partout au Maroc.',
    'footer.about': 'À propos',
    'footer.help': 'Aide',
    'newsletter.stay_informed': 'Restez informé',
    'newsletter.invite': 'Inscrivez-vous pour recevoir nos nouveautés et nos offres.',
    'newsletter.subscribe': 'S’abonner',
    // ── Bandeau de réassurance & CTA collant ────────────────────────────────────────────────────────────
    // Les six phrases de départ de la rangée de réassurance, et les trois du CTA collant — ces dernières
    // étaient ANGLAISES, y compris dans une boutique française.
    'trust.section_title': 'Réassurance',
    'trust.fast_shipping': 'Livraison rapide',
    'trust.fast_shipping_sub': 'Expédié sous 24–48h',
    'trust.cod_sub': 'Payez à réception',
    'trust.easy_returns': 'Retours faciles',
    'trust.easy_returns_sub': 'Sous 14 jours',
    'trust.warranty': 'Garantie',
    'trust.warranty_sub': 'Qualité vérifiée',
    'sticky.default_title': 'Titre du CTA collant',
    'sticky.reviews_placeholder': '(xxxx avis)',
    'common.learn_more': 'En savoir plus',
    'contact.success': 'Merci de nous avoir contactés ! Nous vous répondrons très vite.',
    // ── Collections & héros ─────────────────────────────────────────────────────────────────────────────
    'collection.categories': 'Catégories',
    'collection.view_all': 'Voir tout',
    'hero.shop_now': 'Découvrir',
    'a11y.brand_slideshow': 'Diaporama de notre marque',
    'builder.sample_product_title': 'Titre du produit',
    'cart.loading': 'Chargement du panier…',
    'a11y.decrease_quantity_full': 'Diminuer la quantité',
    'a11y.increase_quantity_full': 'Augmenter la quantité',
    // ── Panier (suite) ──────────────────────────────────────────────────────────────────────────────────
    'cart.undo': 'Annuler',
    'cart.product_column': 'Produit',
    'cart.price_column': 'Prix',
    // ── Quand un bloc casse ─────────────────────────────────────────────────────────────────────────────
    // Rendu sur la VITRINE, pas seulement dans l'éditeur : la frontière d'erreur attrape un bloc qui lève
    // pendant le rendu d'une vraie page. C'était en anglais.
    'error.block_failed': 'Ce bloc n’a pas pu s’afficher.',
    'error.section_failed': 'Cette section n’a pas pu s’afficher.',
    'error.unknown_block': 'Type de bloc inconnu :',
    'error.unknown_section': 'Type de section inconnu :',
    // ── Écrans d'état de la vitrine ─────────────────────────────────────────────────────────────────────
    //
    // Le même jeu de six écrans revient sur cinq routes (collection, catalogue, produit, recherche, page).
    // Quatre routes les rendaient EN ANGLAIS — la 404, le fourre-tout `[...slug]` et les pages statiques —
    // donc un visiteur français tombait sur « Store not found ». Une seule clé par écran, quelle que soit
    // la route qui l'affiche.
    'status.too_many_requests': 'Trop de requêtes',
    'status.too_many_requests_body': 'Vous avez effectué trop de requêtes en peu de temps. Patientez un instant puis réessayez.',
    'status.store_setting_up': 'Boutique en préparation',
    'status.store_setting_up_body': 'Cette boutique arrive bientôt. Revenez dans quelques jours.',
    'status.store_unavailable': 'Boutique indisponible',
    'status.store_unavailable_heading': 'Boutique momentanément indisponible',
    'status.store_unavailable_body': 'Impossible de charger cette boutique pour le moment. Réessayez dans quelques instants.',
    'status.search_unavailable': 'Recherche indisponible',
    'status.search_unavailable_heading': 'Recherche momentanément indisponible',
    // Un corps par route : dire « cette collection » plutôt que « la page » est la seule information que
    // l'écran apporte, puisque le code d'erreur ne dit rien au visiteur.
    'status.collection_unavailable_body': 'Impossible de charger cette collection pour le moment. Réessayez dans quelques instants.',
    'status.collections_unavailable_body': 'Impossible de charger les collections pour le moment. Réessayez dans quelques instants.',
    'status.catalog_unavailable_body': 'Impossible de charger le catalogue pour le moment. Réessayez dans quelques instants.',
    'status.product_unavailable_body': 'Impossible de charger ce produit pour le moment. Réessayez dans quelques instants.',
    'status.page_unavailable_body': 'Impossible de charger cette page pour le moment. Réessayez dans quelques instants.',
    'status.store_not_found': 'Boutique introuvable',
    'status.store_not_found_body': 'Aucune boutique à cette adresse. Vérifiez le lien et réessayez.',
    'status.collection_not_found': 'Collection introuvable',
    'status.collection_not_found_body': 'Aucune collection à cette adresse. Vérifiez le lien et réessayez.',
    'status.product_not_found': 'Produit introuvable',
    'status.product_not_found_body': 'Aucun produit à cette adresse. Vérifiez le lien et réessayez.',
    'status.page_not_found': 'Page introuvable',
    'status.page_not_found_body': 'Aucune page à cette adresse. Vérifiez le lien et réessayez.',
    // ── Titres de page et états vides ───────────────────────────────────────────────────────────────────
    'page.cart_title': 'Mon panier',
    'page.checkout_title': 'Commander',
    'page.collections_title': 'Collections',
    'page.no_collections': 'Aucune collection pour le moment. Revenez bientôt.',
    'search.empty_prompt': 'Saisissez un mot-clé dans la barre de recherche pour trouver un produit.',
    'search.heading': 'Rechercher',
    'search.results_for': 'Résultats pour « {query} »',
    'search.no_match': 'Aucun produit ne correspond à « {query} ».',
    'search.too_many_products': 'Beaucoup de produits dans cette boutique — affinez votre recherche pour de meilleurs résultats.',
    // Le bandeau d'aperçu, vu par le VENDEUR seul (mode preview). Français comme le reste de son admin.
    'preview.collection': 'Aperçu de la collection',
    'preview.product': 'Aperçu du produit',
    'preview.this_theme': 'Ce thème',
    // ── Divers ──────────────────────────────────────────────────────────────────────────────────────────
    // Le nom de repli quand la boutique n'a pas pu être résolue — jamais le nom d'une vraie boutique.
    // ── Contenus de DÉPART des sections (famille B) ──────────────────────────────────────────────────────
    // Ce que le vendeur trouve dans une section qu'il vient d'ajouter, avant de la réécrire. Une partie
    // était ANGLAISE — les onglets, le surtitre du héros, le widget d'avis Facebook — dans toutes les
    // boutiques, y compris françaises.
    'section.icon_bar_item_title': 'Votre avantage',
    'section.icon_bar_intro': 'Des raisons concrètes qui font revenir des milliers de clients.',
    'section.icon_bar_shipping_text': '<p>Expédié sous 24h avec un suivi en temps réel jusqu’à votre porte.</p>',
    'section.icon_bar_payment_text': '<p>Payez à la livraison ou en ligne — vos données restent protégées.</p>',
    'section.icon_bar_returns_text': '<p>Changé d’avis ? Retournez tout article sous 14 jours, sans souci.</p>',
    'section.icon_bar_support_title': 'Une équipe à l’écoute',
    'section.icon_bar_support_text': '<p>Une équipe sympathique prête à vous répondre 7j/7.</p>',
    'section.icon_title': 'Titre de l’icône',
    'section.image_rail_heading': 'Conçu dans le détail',
    'section.image_rail_text': 'Ce qui fait la différence, vu de plus près.',
    'section.image_rail_caption_1': 'Des matières choisies pour durer.',
    'section.image_rail_caption_2': 'Un confort qui se remarque dès le premier jour.',
    'section.image_rail_caption_3': 'Pensé pour le quotidien, testé pour l’usage intensif.',
    'section.image_rail_caption_4': 'La différence se voit dès le premier regard.',
    'section.showcase_image': 'Image de présentation',
    'section.slideshow_subtitle': 'Racontez votre histoire en images.',
    'section.slide1_heading': 'La nouvelle collection est arrivée',
    'section.slide1_subheading': 'Des pièces pensées pour durer, livrées partout au Maroc.',
    'section.slide2_heading': 'Une qualité pensée pour durer',
    'section.slide2_subheading': 'Des matières sélectionnées avec soin, des finitions impeccables.',
    'section.view_shop': 'Voir la boutique',
    'section.header_prefix': 'Surtitre',
    'section.button_label': 'Libellé du bouton',
    'section.hero_text': '<p>Racontez l’histoire de votre marque avec un grand visuel.</p>',
    'section.total_price': 'Prix total :',
    'section.add_selected': 'Ajouter la sélection au panier',
    'section.collapsible_row': 'Ligne dépliable',
    'section.returns_exchanges': 'Retours et échanges',
    'section.need_help': 'Besoin d’aide ?',
    'section.why_us_html': 'Pourquoi <strong>nous</strong> plutôt que les autres',
    'section.others': 'Les autres',
    'section.quality_guaranteed': 'Qualité garantie',
    'section.tab_button': 'Onglet',
    'section.tab_heading': 'Titre de l’onglet',
    'section.content_tabs': 'Onglets de contenu',
    'section.content_tabs_heading': 'des **bienfaits** prouvés',
    'section.why_choose_us': 'Pourquoi nous choisir',
    'section.column_title': 'Titre de la colonne',
    'section.icon_with_text': 'Icône et texte',
    'section.category': 'Catégorie',
    'section.faq_q1': 'Quels sont les délais de livraison ?',
    'section.faq_q2': 'Comment se passe le paiement à la livraison ?',
    'section.faq_q3': 'Puis-je échanger ou retourner un article ?',
    'section.featured_collection': 'Collection en vedette',
    'section.badge_list': 'MEILLEURE AFFAIRE, MEILLEURE VENTE, −[percentage]',
    'section.best_selling': 'Meilleures ventes',
    'section.hero_heading': 'La collection de l’été',
    'section.hero_alt': 'Collection de l’été',
    'section.ticker_free_shipping': 'Livraison offerte dès 400 MAD',
    'section.ticker_new_weekly': 'Nouveautés chaque semaine',
    'section.countdown_title': 'Vente flash',
    'section.countdown_subtitle': 'L’offre se termine dans :',
    'section.countdown_cta': 'J’en profite',
    'section.countdown_expired': 'Offre terminée',
    'section.image_slider': 'Diaporama images / vidéos',
    'section.our_world': 'Notre **univers**',
    'section.noble_materials': 'Matières nobles',
    'section.fine_details': 'Détails soignés',
    'section.insta_stories': 'Stories Instagram',
    'section.insta_stories_text': 'Montrez vos images et vos témoignages dans un format que vos clients connaissent déjà.',
    'section.new_arrival': 'Nouveauté',
    'section.trustpilot_reviews': 'Avis Trustpilot',
    'section.trustpilot_subtitle': 'Excellent [rating] <strong>/ 5</strong> [rating_stars]',
    'section.review_title_1': 'Livraison au top',
    'section.review_title_2': 'Service impeccable',
    'section.review_title_3': 'Très satisfaite',
    'section.brand_placeholder': 'Votre marque',
    'common.show_more': 'Voir plus',
    'common.show_less': 'Voir moins',
    'section.new_benefit': 'Nouvel avantage',
    'section.pairs_well': 'Se marie bien avec',
    'section.your_offer': 'Votre offre',
    'section.review_quote': 'J’adore, je recommande vivement !',
    'section.review_author': 'Sarah M., Casablanca',
    'section.view_all_details': 'Voir tous les détails',
    'section.announcement_free_shipping': 'Livraison offerte partout au Maroc',
    'section.header_announcement': 'Livraison offerte dès 400 MAD · Paiement à la livraison disponible',
    'section.collage': 'Collage multimédia',
    'section.describe_video': 'Décrivez la vidéo',
    'section.collections': 'Collections',
    'section.before': 'Avant',
    'section.after': 'Après',
    'section.blog_posts': 'Articles du blog',
    'section.thought_through': 'Pensé dans le moindre détail',
    'section.shipping_returns': 'Livraison & retours',
    'section.new_badge': 'Nouveau',
    'section.you_may_also_like': 'Vous aimerez aussi',
    'section.result_1': 'Peau plus douce',
    'section.result_2': 'Hydratation durable',
    'section.result_3': 'La recommanderaient',
    'section.shoppable_image': 'Image cliquable',
    'section.compare_more': 'Comparer d’autres produits',
    'section.product_name': 'Nom du produit',
    'section.verified_customer_f': 'Cliente vérifiée',
    'section.verified_customer_m': 'Client vérifié',
    'section.tracking_number': 'Numéro de suivi',
    'countdown.days': 'JOURS',
    'countdown.hours': 'HEURES',
    'countdown.minutes': 'MIN',
    'countdown.seconds': 'SEC',
    'sales_pop.someone': 'Quelqu’un',
    'sales_pop.right_now': 'En ce moment',
    'sales_pop.people_viewing': '{count} personnes regardent cette boutique',
    'sales_pop.live_data': 'données en direct',
    'a11y.collection': 'Collection',
    'a11y.delivery_timeline': 'Calendrier de livraison',
    'section.day_labels': 'Lun, Mar, Mer, Jeu, Ven, Sam, Dim',
    'section.tab_material': 'Matière',
    'section.tab_material_text': '<p>Informations sur la matière</p>',
    'section.text_with_icon': 'Texte avec icône',
    'section.day_names': 'Lundi, Mardi, Mercredi, Jeudi, Vendredi, Samedi, Dimanche',
    'section.month_names': 'Janvier, Février, Mars, Avril, Mai, Juin, Juillet, Août, Septembre, Octobre, Novembre, Décembre',
    'section.month_labels': 'Jan, Fév, Mars, Avr, Mai, Juin, Juil, Août, Sep, Oct, Nov, Déc',
    'section.tabs_heading': 'Ils nous font confiance',
    'section.tab1_heading': 'Un titre pour le premier onglet',
    'section.tab2_heading': 'Un titre pour le deuxième onglet',
    'section.tab3_heading': 'Un titre pour le troisième onglet',
    'section.tab_subheading': 'Une courte phrase de présentation',
    'section.tab1_label': 'Onglet 1',
    'section.tab2_label': 'Onglet 2',
    'section.tab3_label': 'Onglet 3',
    'section.tab1_text': '<p>Décrivez ici un atout, une histoire ou un témoignage. Ajoutez un bouton et, si vous le souhaitez, une fiche produit.</p>',
    'section.tab2_text': '<p>Chaque onglet a sa propre image et son propre contenu.</p>',
    'section.tab3_text': '<p>On change d’onglet sans aucun code.</p>',
    'section.author_name': 'Nom de l’auteur',
    'section.comment_author': 'Auteur du commentaire',
    // Les quatre mots du widget d'avis façon réseau social — lus par l'acheteur, donc traduits.
    'social.like': 'J’aime',
    'social.comment': 'Commenter',
    'social.reply': 'Répondre',
    'social.share': 'Partager',
    'common.shop': 'Boutique',
    // ── Avis produits (feature 0066) — les quatre phrases qu'un ACHETEUR lit sous un produit.
    'reviews.count': {
        one: '{count} avis',
        other: '{count} avis',
    },
    'reviews.read_all': {
        one: 'Lire {count} avis',
        other: 'Lire les {count} avis',
    },
    'reviews.verified': 'Vérifié',
    'reviews.helpful': 'Utile',
    'reviews.anonymous': 'Anonyme',
    'reviews.seller_reply': 'Réponse du vendeur',
    'reviews.write': 'Écrire un avis',
    'reviews.pager.label': 'Pagination des avis',
    'reviews.pager.previous': 'Page précédente',
    'reviews.pager.next': 'Page suivante',
    'reviews.pager.page': 'Page {page}',
    'reviews.form_rating': 'Votre note',
    'reviews.form_name': 'Votre nom (facultatif)',
    'reviews.form_title': 'Titre (facultatif)',
    'reviews.form_body': 'Votre avis',
    'reviews.form_submit': 'Envoyer mon avis',
    'reviews.form_note': 'La boutique lit votre avis avant qu’il apparaisse.',
    'reviews.form_thanks': 'Merci ! Votre avis est bien arrivé à la boutique.',
    'reviews.form_error': 'Nous n’avons pas pu envoyer votre avis. Réessayez.',
    'section.feature_gallery_heading': 'Pensé dans le moindre détail',
    'section.colorways_heading': 'Chaque couleur est la vôtre',
    'section.flip_cards_heading': 'Ce qui fait la différence',
    'section.flip_cards_c1_title': 'Fait pour durer',
    'section.flip_cards_c1_text': 'Des matériaux choisis et un assemblage honnête : conçu pour servir tous les jours, et pour être réparé plutôt que remplacé.',
    'section.flip_cards_c2_title': 'Vérifié, pas promis',
    'section.flip_cards_c2_caption': 'de clients commandent à nouveau',
    'section.flip_cards_c2_text': 'Chaque lot passe les mêmes contrôles avant l’expédition. Le chiffre est la part de clients qui ont commandé à nouveau au cours des douze derniers mois.',
    'section.flip_cards_c3_title': 'Livré avec soin',
    'section.flip_cards_c3_text': 'Emballé à la main, expédié sous 24 heures, suivi jusqu’à votre porte. Si quelque chose arrive abîmé, nous le réglons.',
    'section.feature_gallery_title_1': 'Matières nobles',
    'section.feature_gallery_text_1': 'Des matières choisies pour durer.',
    'section.feature_gallery_title_2': 'Détails soignés',
    'section.feature_gallery_text_2': 'Un confort qui se remarque dès le premier jour.',
    'section.feature_gallery_title_3': 'Qualité garantie',
    'section.feature_gallery_text_3': 'Pensé pour le quotidien, testé pour l’usage intensif.',
    // Feature 0078 lot B — produits numériques : caisse, fiche produit, page Merci, page de téléchargement.
    'checkout.email_for_files': 'E-mail (vos fichiers y seront envoyés)',
    'checkout.email_needed': 'Indiquez votre e-mail : vos fichiers y seront envoyés.',
    'checkout.digital_card_only': 'Les produits numériques se paient par carte, et cette boutique n’accepte pas la carte pour le moment.',
    'checkout.digital_nothing_to_ship': 'Rien à livrer : vos fichiers arrivent par e-mail juste après le paiement.',
    'product.digital_unavailable': 'Ce produit numérique ne peut pas être acheté sur cette boutique pour le moment.',
    'product.digital_note': 'Produit numérique : vous le recevez par e-mail juste après le paiement.',
    'downloads.title': 'Vos fichiers',
    'downloads.pending': 'Paiement en cours de confirmation… Vos fichiers apparaîtront ici dans quelques secondes.',
    'downloads.sent_to': 'Un e-mail avec vos fichiers a été envoyé à {email}.',
    'downloads.later': 'Vos fichiers vous seront envoyés par e-mail à {email}.',
    'downloads.download': 'Télécharger',
    'downloads.open': 'Ouvrir',
    'downloads.remaining': 'Téléchargements restants : {count}',
    'downloads.exhausted': 'Limite de téléchargements atteinte',
    'downloads.failed': 'Le téléchargement n’a pas pu démarrer. Réessayez.',
    'downloads.page_title': 'Vos fichiers — commande {number}',
    'downloads.page_intro': 'Les fichiers de votre commande {number} chez {store}.',
    'downloads.not_found_title': 'Lien introuvable',
    'downloads.not_found': 'Ce lien de téléchargement n’existe pas ou n’est plus actif. Contactez la boutique si vous pensez qu’il s’agit d’une erreur.',
    'downloads.keep_email': 'Gardez l’e-mail reçu : son lien vous ramène à cette page.',
};
//# sourceMappingURL=fr.js.map